import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";

import { api, AccountOut, MeOut } from "@/api/client";
import { clearUserCache } from "./sessionCache";
import { setUsageUser } from "@/usage";

type Status = "loading" | "authed" | "unauthed";
export type TradingEnvironment = "paper" | "live";

interface AuthState {
  status: Status;
  me: MeOut | null;
  /** 全局交易环境；研究行情共用，账户/持仓/订单/策略按环境隔离。 */
  tradingEnvironment: TradingEnvironment;
  setTradingEnvironment: (environment: TradingEnvironment) => void;
  environmentAccounts: AccountOut[];
  /** 当前环境中选中的账户。登录后不再使用跨环境“全部账户聚合”。 */
  currentAccountId: number | null;
  setCurrentAccountId: (id: number | null) => void;
  /** 当前选中账户 detail；全部聚合时为 null */
  currentAccount: AccountOut | null;
  login: (username: string, password: string, remember?: boolean) => Promise<void>;
  register: (req: { invite_code: string; username: string; password: string; display_name?: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const STORAGE_KEY = "ws.currentAccountId";
const ENV_STORAGE_KEY = "ws.tradingEnvironment";

function readStoredEnvironment(): TradingEnvironment {
  try {
    return localStorage.getItem(ENV_STORAGE_KEY) === "live" ? "live" : "paper";
  } catch {
    return "paper";
  }
}

function readStoredAccount(): number | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v == null || v === "") return null;
    if (v === "all") return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function persistAccount(id: number | null): void {
  try {
    localStorage.setItem(STORAGE_KEY, id == null ? "all" : String(id));
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const identity = useRef<number | null>(null);
  const clearSessionCache = useCallback(async () => {
    setUsageUser(null);
    await clearUserCache(queryClient);
  }, [queryClient]);
  const [status, setStatus] = useState<Status>("loading");
  const [me, setMe] = useState<MeOut | null>(null);
  const [currentAccountId, setCurrentAccountIdState] = useState<number | null>(readStoredAccount());
  const [tradingEnvironment, setTradingEnvironmentState] = useState<TradingEnvironment>(readStoredEnvironment());

  const selectEnvironmentAccount = useCallback((m: MeOut, environment: TradingEnvironment) => {
    const matches = m.accounts.filter((account) => account.active && account.is_paper === (environment === "paper"));
    const stored = readStoredAccount();
    const selected = matches.find((account) => account.id === stored)
      ?? matches.find((account) => account.is_default)
      ?? matches[0]
      ?? null;
    setCurrentAccountIdState(selected?.id ?? null);
    persistAccount(selected?.id ?? null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const m = await api.authMe();
      if (m) {
        if (identity.current !== m.id) await clearSessionCache();
        identity.current = m.id;
        setMe(m);
        setUsageUser(m.id);
        setStatus("authed");
        const environment = readStoredEnvironment();
        setTradingEnvironmentState(environment);
        selectEnvironmentAccount(m, environment);
      } else {
        identity.current = null;
        await clearSessionCache();
        setMe(null);
        setStatus("unauthed");
      }
    } catch {
      setUsageUser(null);
      setMe(null);
      setStatus("unauthed");
    }
  }, [selectEnvironmentAccount, clearSessionCache]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setCurrentAccountId = useCallback((id: number | null) => {
    if (id == null) return;
    const account = me?.accounts.find((item) => item.id === id && item.active);
    if (!account || account.is_paper !== (tradingEnvironment === "paper")) return;
    setCurrentAccountIdState(id);
    persistAccount(id);
  }, [me, tradingEnvironment]);

  const setTradingEnvironment = useCallback((environment: TradingEnvironment) => {
    if (!me) return;
    setTradingEnvironmentState(environment);
    localStorage.setItem(ENV_STORAGE_KEY, environment);
    selectEnvironmentAccount(me, environment);
    window.dispatchEvent(new CustomEvent("whaleseek:trading-environment", { detail: { environment } }));
  }, [me, selectEnvironmentAccount]);

  const login = useCallback(async (username: string, password: string, remember = false) => {
    await clearSessionCache();
    const m = await api.authLogin(username, password, remember);
    identity.current = m.id;
    setMe(m);
    setStatus("authed");
    const environment: TradingEnvironment = m.accounts.some((account) => account.active && account.is_paper) ? "paper" : "live";
    setTradingEnvironmentState(environment);
    localStorage.setItem(ENV_STORAGE_KEY, environment);
    selectEnvironmentAccount(m, environment);
  }, [selectEnvironmentAccount, clearSessionCache]);

  const register = useCallback(
    async (req: { invite_code: string; username: string; password: string; display_name?: string }) => {
      await clearSessionCache();
      const m = await api.authRegister(req);
      identity.current = m.id;
      setMe(m);
      setStatus("authed");
      const environment: TradingEnvironment = m.accounts.some((account) => account.active && account.is_paper) ? "paper" : "live";
      setTradingEnvironmentState(environment);
      localStorage.setItem(ENV_STORAGE_KEY, environment);
      selectEnvironmentAccount(m, environment);
    },
    [selectEnvironmentAccount, clearSessionCache],
  );

  const logout = useCallback(async () => {
    setUsageUser(null);
    try {
      await api.authLogout();
    } catch {
      /* ignore */
    }
    identity.current = null;
    setMe(null);
    setStatus("unauthed");
    await clearSessionCache();
    setCurrentAccountIdState(null);
    persistAccount(null);
  }, [clearSessionCache]);

  const currentAccount =
    me && currentAccountId != null
      ? me.accounts.find((a) => a.id === currentAccountId) ?? null
      : null;
  const environmentAccounts = me?.accounts.filter(
    (account) => account.active && account.is_paper === (tradingEnvironment === "paper"),
  ) ?? [];

  return (
    <AuthContext.Provider
      value={{
        status,
        me,
        tradingEnvironment,
        setTradingEnvironment,
        environmentAccounts,
        currentAccountId,
        setCurrentAccountId,
        currentAccount,
        login,
        register,
        logout,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
